/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'

interface Props {
  icon: React.ReactNode
  /** Lower case; rendered in Title Case. */
  label: string
  value: string
  caption: string
}

/**
 * One figure in the summary card's stat row: label, value, caption.
 *
 * Takes three rows from the parent grid (`subgrid`), so it must sit in a grid
 * laid out in threes — the card's stat grid.
 */
const SummaryStatTile = ({ icon, label, value, caption }: Props) => (
  <div css={tileStyle}>
    <span css={labelRowStyle}>
      <span css={iconStyle} aria-hidden='true'>
        {icon}
      </span>
      <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
        {capitalizeWords(label)}
      </Text>
    </span>
    <Text variant='paragraphLarge' weight='semiBold' color={colors.WHITE}>
      {value}
    </Text>
    <Text variant='paragraphXSmall' weight='regular' color={colors.NEUTRAL_80}>
      {caption}
    </Text>
  </div>
)

export default SummaryStatTile

const tileStyle = css`
  display: grid;
  grid-row: span 3;
  grid-template-rows: subgrid;
  row-gap: 4px;
  min-width: 0;
  padding: 12px;
  border-radius: 8px;
  background-color: ${colors.NEUTRAL_600};
`

/* Top-aligned, so a label that wraps keeps its icon on the first line. */
const labelRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  color: ${colors.NEUTRAL_50};
`

/* Sized to the 14px label it sits beside; the 4px drop centres it on the
   label's ~20px first line. */
const iconStyle = css`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 4px;
  font-size: 14px;
`
